import { atom, createStore, useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { countryEnum, type CommodityType, type MatchingResolution, matchingResolutionEnum } from '~/schemas/enums';
import type { RegistryIntegrationMode } from '~/schemas/organizations';

export interface UserConfig {
  commodityTypes: CommodityType[];
  countryCode: string;
  isEndConsumer: boolean;
  logoUrl: string | undefined;
  orgName: string;
  timezone: string;
  matchingResolutions: MatchingResolution[];
  welcomeScreenEnabled: boolean;
  hideProductionDevicesData: boolean;
  hideProductionDevicesFromReports: boolean;
  featureFlags: Record<string, boolean | string | number>;
  dealsEnabled: boolean;
  esnaEnabled: boolean;
  esnaRequireLesPdfForActivation: boolean;
  ssoManagedUsers: boolean;
  // ISO date, or undefined when the organisation locks no portfolio history.
  portfolioLockedFrom: string | undefined;
  // Per-registry push-integration mode, keyed by registry identifier. Absent registry ⇒ disabled.
  registryIntegration: Record<string, RegistryIntegrationMode>;
}

export const userConfig = atom<UserConfig>({
  commodityTypes: ['ELECTRICITY'],
  countryCode: countryEnum.enum.GB,
  featureFlags: {},
  hideProductionDevicesData: false,
  hideProductionDevicesFromReports: false,
  isEndConsumer: false,
  logoUrl: undefined,
  matchingResolutions: [],
  orgName: 'dev-UK',
  timezone: 'Europe/London',
  welcomeScreenEnabled: false,
  dealsEnabled: false,
  esnaEnabled: false,
  esnaRequireLesPdfForActivation: false,
  ssoManagedUsers: false,
  portfolioLockedFrom: undefined,
  registryIntegration: {},
});

export const userStore = createStore();

export const useUserConfig = () => {
  const user = useAtomValue(userConfig);

  const hasYearFromApril = useMemo(
    () => user.matchingResolutions?.includes(matchingResolutionEnum.enum.yearly_from_april) || false,
    [user.matchingResolutions],
  );

  const hasYearFromMay = useMemo(
    () => user.matchingResolutions?.includes(matchingResolutionEnum.enum.yearly_from_may) || false,
    [user.matchingResolutions],
  );

  return { hasYearFromApril, hasYearFromMay, user };
};
